import React from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GoBackArrowBtn } from '@/components/GoBackArrowBtn/GoBackArrowBtn';
import useScreenSize from '@/hooks/useScreenSize';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';

import styles from './styles.module.css';

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  goBack?: number;
  alternative?: boolean;
  onBack?: () => void;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, goBack, alternative, onBack }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { windowWidth } = useScreenSize();
  const parentLevelIndex = -2;
  const parent = items.at(parentLevelIndex);

  const handleNavigate = (item: BreadcrumbItem | undefined) => {
    // `onBack` ramène vers un état d'écran, pas une route : naviguer en plus renverrait sur la même
    // URL sans state et ferait échouer la garde d'entrée de la page. Même contrat que GoBackArrowBtn.
    if (onBack) {
      onBack();
      return;
    }
    if (goBack) {
      navigate(goBack);
    } else {
      navigate(item?.path || '/', { state: { key: parent?.key } });
    }
  };

  return (
    <>
      {windowWidth < 900 ? (
        <div className={styles.goBackArrowBtnContainer}>
          <GoBackArrowBtn parent={parent} goBack={goBack} alternative={alternative} onBack={onBack} />
        </div>
      ) : (
        <nav aria-label="breadcrumb">
          <ol className={styles.breadcrumb}>
            <li>
              <button
                className={styles.backArrowLeft}
                aria-label={t('common.return')}
                onClick={() => handleNavigate(parent)}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleNavigate(parent);
                  }
                }}
              >
                <i className="totem-icon totem-icon-arrow-left-big" />
              </button>
            </li>

            {items.map((item, index) => (
              <li
                key={item.label}
                className={`${styles.breadcrumbItem} ${index === items.length - 1 ? styles.active : ''}`}
                aria-current={index === items.length - 1 ? 'page' : undefined}
              >
                {index === items.length - 1 ? (
                  item.label
                ) : (
                  <button
                    className={styles.textCrumbs}
                    onClick={() => {
                      handleNavigate(item);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleNavigate(item);
                      }
                    }}
                    tabIndex={0}
                    aria-label={`Naviguer vers ${item.label}`}
                  >
                    {item.label}
                  </button>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
    </>
  );
};

export default Breadcrumb;
